/* Find Us section: Google Maps + Places Autocomplete + Nearest Stores */
(function () {
  'use strict';

  // Deduplicated Google Maps loader
  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve(window.google);
    }
    if (window.__gmapsPromise) {
      return window.__gmapsPromise;
    }
    if (!apiKey) {
      return Promise.reject(new Error('Missing Google Maps API key'));
    }
    window.__gmapsPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places&v=weekly';
      script.async = true;
      script.defer = true;
      script.onload = function () {
        if (window.google && window.google.maps) {
          resolve(window.google);
        } else {
          reject(new Error('Google Maps failed to load'));
        }
      };
      script.onerror = function () {
        reject(new Error('Failed to load Google Maps script'));
      };
      document.head.appendChild(script);
    });
    return window.__gmapsPromise;
  }

  function parseJsonFromScript(scriptId) {
    try {
      var el = document.getElementById(scriptId);
      if (!el) return null;
      var json = el.textContent || el.innerText || '';
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  }

  function announce(messageEl, message) {
    if (!messageEl) return;
    messageEl.textContent = message;
    messageEl.hidden = !message;
  }

  function formatDistance(distanceMeters) {
    // UK audience: show miles by default
    var miles = distanceMeters / 1609.344;
    if (miles < 0.1) {
      return (distanceMeters / 1000).toFixed(2) + ' km';
    }
    return miles.toFixed(1) + ' miles';
  }

  function toLatLng(obj) {
    if (!obj) return null;
    if (typeof obj.lat === 'function') {
      return { lat: obj.lat(), lng: obj.lng() };
    }
    return { lat: Number(obj.lat), lng: Number(obj.lng) };
  }

  function computeDistanceMeters(a, b) {
    // Haversine
    var R = 6371000; // meters
    var lat1 = a.lat * Math.PI / 180;
    var lat2 = b.lat * Math.PI / 180;
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var sinDLat = Math.sin(dLat / 2);
    var sinDLng = Math.sin(dLng / 2);
    var c = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    var d = 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
    return R * d;
  }

  function buildGoogleDirectionsUrl(location) {
    var dest = encodeURIComponent(location.lat + ',' + location.lng);
    var name = location.name ? encodeURIComponent(location.name) : '';
    var base = 'https://www.google.com/maps/dir/?api=1';
    if (location.google_place_id) {
      return base + '&destination_place_id=' + encodeURIComponent(location.google_place_id) + '&destination=' + dest + (name ? '&destination_name=' + name : '');
    }
    return base + '&destination=' + dest + (name ? '&destination_name=' + name : '');
  }

  function buildAppleMapsUrl(location, originLatLng) {
    // Apple Maps directions with labelled destination and optional origin
    // Use daddr as "Label@lat,lng" to prevent mislabelling
    var base = 'https://maps.apple.com/?';
    var label = (location && location.name) ? String(location.name) : 'Destination';
    var daddrPart;
    if (location && location.apple_maps_query) {
      daddrPart = 'daddr=' + encodeURIComponent(location.apple_maps_query);
    } else {
      daddrPart = 'daddr=' + encodeURIComponent(label + '@' + location.lat + ',' + location.lng);
    }
    var saddrPart = originLatLng && isFinite(originLatLng.lat) && isFinite(originLatLng.lng)
      ? 'saddr=' + encodeURIComponent(originLatLng.lat + ',' + originLatLng.lng)
      : 'saddr=Current%20Location';
    return base + saddrPart + '&' + daddrPart + '&dirflg=d';
  }

  function createResultItem(sectionId, location, distanceMeters, originLatLng, showOnMapLabel) {
    var distance = typeof distanceMeters === 'number' ? formatDistance(distanceMeters) : '';
    var googleUrl = buildGoogleDirectionsUrl(location);
    var appleUrl = buildAppleMapsUrl(location, originLatLng);
    var phoneHtml = location.phone ? '<div class="find-us__result-meta"><a href="tel:' + encodeURIComponent(location.phone) + '">' + location.phone + '</a></div>' : '';
    var websiteHtml = location.website ? '<div class="find-us__result-meta"><a href="' + location.website + '" target="_blank" rel="noopener">Website</a></div>' : '';
    var distanceHtml = distance ? '<div class="find-us__result-distance">' + distance + '</div>' : '';

    var html = '' +
      '<article class="find-us__result" data-location-id="' + location.id + '">' +
      '  <div class="find-us__result-header">' +
      '    <p class="find-us__result-title font-type-3">' + (location.name || 'Showroom') + '</p>' +
      '    ' + distanceHtml +
      '  </div>' +
      '  <div class="find-us__result-address">' + (location.address || '') + '</div>' +
      '  ' + phoneHtml +
      '  ' + websiteHtml +
      '  <div class="find-us__result-actions">' +
      '    <a class="secondary-btn" href="' + googleUrl + '" target="_blank" rel="noopener">Google Maps</a>' +
      '    <a class="secondary-btn" href="' + appleUrl + '" target="_blank" rel="noopener">Apple Maps</a>' +
      '    <button class="secondary-btn" data-action="show-on-map" data-location-id="' + location.id + '">' + (showOnMapLabel || 'Show on map') + '</button>' +
      '  </div>' +
      '</article>';
    return html;
  }

  function initFindUsSection(sectionEl) {
    var sectionId = sectionEl.getAttribute('data-section-id');
    var data = parseJsonFromScript('find-us-data-' + sectionId);
    var resultsEl = document.getElementById('find-us-results-' + sectionId);
    var messagesEl = document.getElementById('find-us-messages-' + sectionId);
    var inputEl = document.getElementById('find-us-input-' + sectionId);
    var useLocationBtn = sectionEl.querySelector('.find-us__button.use-location');
    var mapEl = document.getElementById('find-us-map-' + sectionId);

    if (!data) {
      announce(messagesEl, 'Configuration error: could not read section data.');
      return;
    }

    var locations = Array.isArray(data.locations) ? data.locations.map(function (l) {
      if (!l) return null;
      var lat = Number(l.lat);
      var lng = Number(l.lng);
      if (!isFinite(lat) || !isFinite(lng)) return null;
      return {
        id: l.id,
        name: l.name || '',
        address: l.address || '',
        lat: lat,
        lng: lng,
        phone: l.phone || '',
        website: l.website || '',
        google_place_id: l.google_place_id || '',
        apple_maps_query: l.apple_maps_query || '',
        pinned: !!l.pinned,
        country: (l.country || '').toString().toUpperCase()
      };
    }).filter(function (x) { return !!x; }) : [];

    if (!data.apiKey) {
      announce(messagesEl, 'Google Maps API key is missing. Please add it in the section settings.');
      return;
    }

    loadGoogleMaps(data.apiKey).then(function (google) {
      // Prepare map options
      var options = {
        center: toLatLng(data.mapOptions && data.mapOptions.center) || { lat: 51.509865, lng: -0.118092 },
        zoom: Number((data.mapOptions && data.mapOptions.zoom) || 6),
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      };
      // Optional styles
      if (data.mapOptions && data.mapOptions.styles) {
        try {
          var styles = data.mapOptions.styles;
          if (typeof styles === 'string' && styles.trim()) {
            styles = JSON.parse(styles);
          }
          if (Array.isArray(styles)) {
            options.styles = styles;
          }
        } catch (e) {
          // ignore invalid styles
        }
      }

      var map = new google.maps.Map(mapEl, options);
      var infoWindow = new google.maps.InfoWindow();
      var bounds = new google.maps.LatLngBounds();
      var markersById = Object.create(null);
      var maxResults = Number(data.maxResults || 5);
      var preferSameCountry = !!data.preferSameCountry;
      var userMarker = null;
      var userCircle = null;

      var currentOrigin = null;
      var currentOriginCountry = null;

      function getCountryFromComponents(components) {
        if (!components) return null;
        for (var i = 0; i < components.length; i++) {
          var comp = components[i];
          if (comp && comp.types && comp.types.indexOf('country') !== -1) {
            return (comp.short_name || comp.long_name || '').toString().toUpperCase();
          }
        }
        return null;
      }

      function sortLocationsWithPreference(originLatLng, originCountry) {
        var enriched = locations.map(function (loc) {
          var distance = originLatLng ? computeDistanceMeters(originLatLng, { lat: loc.lat, lng: loc.lng }) : null;
          var sameCountry = originCountry && loc.country && loc.country === originCountry;
          return { location: loc, distance: distance, sameCountry: !!sameCountry };
        });
        if (!originLatLng) return enriched; // keep original order when no origin

        // Fallback: if we prefer same-country but couldn't detect originCountry,
        // infer it from the nearest store.
        if (preferSameCountry && originLatLng && !originCountry && enriched.length > 0) {
          var nearest = enriched.slice().sort(function (a, b) { return a.distance - b.distance; })[0];
          if (nearest && nearest.location && nearest.location.country) {
            originCountry = nearest.location.country;
            enriched.forEach(function (e) { e.sameCountry = (e.location.country === originCountry); });
          }
        }

        // If preferring same country and at least one in-country store exists,
        // show all in-country (by distance), then cross-border stores that are
        // closer than the nearest in-country store.
        if (preferSameCountry && originCountry) {
          var inCountry = enriched.filter(function (e) { return e.sameCountry; });
          if (inCountry.length > 0) {
            inCountry.sort(function (a, b) { return a.distance - b.distance; });
            var nearestInDistance = inCountry[0].distance;
            var crossQualified = enriched.filter(function (e) { return !e.sameCountry && e.distance < nearestInDistance; })
              .sort(function (a, b) { return a.distance - b.distance; });
            return inCountry.concat(crossQualified);
          }
        }

        // Fallback: pure distance order
        return enriched.sort(function (a, b) { return a.distance - b.distance; });
      }

      function renderResultsFromOrigin(originLatLng, originCountry) {
        currentOrigin = originLatLng || null;
        currentOriginCountry = originCountry || null;
        if (!resultsEl) return;
        if (!locations.length) {
          resultsEl.innerHTML = '<p>No locations available.</p>';
          return;
        }

        var limited;
        if (!originLatLng) {
          // Default list: only pinned locations, in original order
          var pinned = locations.filter(function (l) { return !!l.pinned; }).map(function (loc) { return { location: loc, distance: null }; });
          limited = pinned.slice(0, maxResults);
        } else {
          var enriched = sortLocationsWithPreference(originLatLng, originCountry);
          limited = enriched.slice(0, maxResults);
        }
        var showOnMapLabel = data.showOnMapLabel || 'Show on map';
        var html = limited.map(function (e) {
          return createResultItem(sectionId, e.location, e.distance, originLatLng, showOnMapLabel);
        }).join('');
        resultsEl.innerHTML = html;
      }

      function attachResultInteractions() {
        if (!resultsEl) return;
        resultsEl.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-action="show-on-map"]');
          if (!btn) return;
          var locId = btn.getAttribute('data-location-id');
          var marker = markersById[locId];
          if (marker) {
            map.panTo(marker.getPosition());
            map.setZoom(Math.max(map.getZoom(), 14));
            google.maps.event.trigger(marker, 'click');
          }
        });
      }

      function fitToOriginAndNearest(originLatLng, originCountry) {
        try {
          if (!originLatLng || !locations || locations.length === 0) {
            return;
          }
          var ordered = sortLocationsWithPreference(originLatLng, originCountry);
          var take = Math.min(Math.max(1, maxResults), ordered.length);
          var fitBounds = new google.maps.LatLngBounds();
          fitBounds.extend(new google.maps.LatLng(originLatLng.lat, originLatLng.lng));
          for (var i = 0; i < take; i++) {
            var p = ordered[i].location;
            fitBounds.extend(new google.maps.LatLng(p.lat, p.lng));
          }
          map.fitBounds(fitBounds);
        } catch (e) {
          // Fallback: do nothing
        }
      }

      // Create markers
      locations.forEach(function (loc) {
        var marker = new google.maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          map: map,
          title: loc.name || 'Showroom'
        });
        markersById[loc.id] = marker;
        bounds.extend(marker.getPosition());
        marker.addListener('click', function () {
          var content = '' +
            '<div class="find-us__infowindow">' +
            '<div class="find-us__infowindow-title">' + (loc.name || 'Showroom') + '</div>' +
            (loc.address ? '<div class="find-us__infowindow-address">' + loc.address + '</div>' : '') +
            (loc.phone ? '<div class="find-us__infowindow-meta"><a href="tel:' + encodeURIComponent(loc.phone) + '">' + loc.phone + '</a></div>' : '') +
            (loc.website ? '<div class="find-us__infowindow-meta"><a href="' + loc.website + '" target="_blank" rel="noopener">Website</a></div>' : '') +
            '<div class="find-us__infowindow-actions">' +
            '<a href="' + buildGoogleDirectionsUrl(loc) + '" target="_blank" rel="noopener">Google Maps</a>' +
            '<a href="' + buildAppleMapsUrl(loc, currentOrigin) + '" target="_blank" rel="noopener">Apple Maps</a>' +
            '</div>' +
            '</div>';
          infoWindow.setContent(content);
          infoWindow.open(map, marker);
        });
      });

      // Fit to markers (or default center if none)
      if (locations.length > 1) {
        map.fitBounds(bounds);
      } else if (locations.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(Math.max(options.zoom, 14));
      }

      // Initial render (no origin)
      renderResultsFromOrigin(null, null);
      attachResultInteractions();

      // Places Autocomplete
      if (inputEl) {
        try {
          var autocomplete = new google.maps.places.Autocomplete(inputEl, {
            fields: ['geometry', 'name', 'formatted_address', 'address_components']
          });
          autocomplete.addListener('place_changed', function () {
            var place = autocomplete.getPlace();
            if (!place || !place.geometry) {
              announce(messagesEl, 'Please select a location from the suggestions.');
              return;
            }
            announce(messagesEl, '');
            var origin = toLatLng(place.geometry.location);
            var originCountry = getCountryFromComponents(place.address_components);
            if (!origin) return;
            // Show a user marker
            if (userMarker) userMarker.setMap(null);
            if (userCircle) userCircle.setMap(null);
            userMarker = new google.maps.Marker({ position: origin, map: map, title: 'Your location', icon: { path: google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: '#1a73e8', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 } });
            renderResultsFromOrigin(origin, originCountry);
            fitToOriginAndNearest(origin, originCountry);
          });
        } catch (e) {
          // Autocomplete may fail if Places not loaded; already included via libraries
        }
      }

      // Use my location (Geolocation API)
      if (useLocationBtn && navigator.geolocation) {
        useLocationBtn.addEventListener('click', function () {
          announce(messagesEl, 'Locating…');
          navigator.geolocation.getCurrentPosition(function (pos) {
            announce(messagesEl, '');
            var origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            // Show a user marker & small accuracy circle
            if (userMarker) userMarker.setMap(null);
            if (userCircle) userCircle.setMap(null);
            userMarker = new google.maps.Marker({ position: origin, map: map, title: 'Your location', icon: { path: google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: '#1a73e8', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 } });
            var radius = Math.min(Math.max(pos.coords.accuracy || 0, 50), 2000); // clamp 50m - 2km
            userCircle = new google.maps.Circle({ strokeColor: '#1a73e8', strokeOpacity: 0.15, strokeWeight: 1, fillColor: '#1a73e8', fillOpacity: 0.08, map: map, center: origin, radius: radius });
            // Reverse geocode to get country, then render with preference
            try {
              var geocoder = new google.maps.Geocoder();
              geocoder.geocode({ location: origin }, function (results, status) {
                var originCountry = null;
                if (status === 'OK' && results && results.length) {
                  for (var r = 0; r < results.length && !originCountry; r++) {
                    originCountry = getCountryFromComponents(results[r].address_components) || originCountry;
                  }
                }
                renderResultsFromOrigin(origin, originCountry);
                fitToOriginAndNearest(origin, originCountry);
              });
            } catch (e) {
              renderResultsFromOrigin(origin, null);
              fitToOriginAndNearest(origin, null);
            }
          }, function (err) {
            announce(messagesEl, 'Unable to get your location: ' + (err && err.message ? err.message : 'Permission denied'));
          }, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
        });
      }

    }).catch(function (err) {
      announce(messagesEl, 'Map failed to load. ' + (err && err.message ? err.message : ''));
    });
  }

  function initAllSections() {
    var sections = document.querySelectorAll('[data-section-type="find-us"]');
    if (!sections || sections.length === 0) return;
    sections.forEach(function (section) { initFindUsSection(section); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    // Run after current frame to allow Liquid to place JSON data
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(initAllSections);
    } else {
      setTimeout(initAllSections, 0);
    }
  }
})();


